import {
  isWeekend as dateFnsIsWeekend,
  addDays,
  subDays,
  isSameDay,
  startOfDay
} from "date-fns";

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a weekend
 */
export const isWeekend = date => dateFnsIsWeekend(date);

/**
 * Check if a date is a business day (Monday-Friday, not on weekends)
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a business day
 */
export const isBusinessDay = date => !isWeekend(date);

/**
 * Check if a date is in the past (before today)
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is before today (today is considered valid)
 */
export const isPastDate = date => startOfDay(date) < startOfDay(new Date());

/**
 * Comprehensive validation for booking dates
 * @param {Date} date - The date to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.allowPast - Allow past dates (default: false)
 * @param {boolean} options.allowWeekends - Allow weekends (default: false)
 * @param {Date[]} options.excludeDates - Array of specific dates to exclude
 * @param {Date[]} options.publicHolidays - Array of public holidays to exclude
 * @returns {boolean} - True if the date is valid for booking
 */
export const isValidBookingDate = (date, options = {}) => {
  const {
    allowPast = false,
    allowWeekends = false,
    excludeDates = [],
    publicHolidays = []
  } = options;

  // Check if date is in the past
  if (!allowPast && isPastDate(date)) {
    return false;
  }

  // Check if date is a weekend
  if (!allowWeekends && isWeekend(date)) {
    return false;
  }

  // Check if date is in excluded dates
  if (excludeDates.some(excludeDate => isSameDay(date, excludeDate))) {
    return false;
  }

  // Check if date is a public holiday
  if (publicHolidays.some(holiday => isSameDay(date, holiday))) {
    return false;
  }

  return true;
};

/**
 * Get the next available business day from a given date
 * @param {Date} startDate - The starting date
 * @param {Object} options - Options for business day calculation
 * @param {Date[]} options.excludeDates - Dates to exclude
 * @param {Date[]} options.publicHolidays - Public holidays to exclude
 * @returns {Date} - The next available business day
 */
export const getNextBusinessDay = (startDate, options = {}) => {
  const { excludeDates = [], publicHolidays = [] } = options;
  let nextDay = addDays(startDate, 1);

  while (!isValidBookingDate(nextDay, {
    allowPast: true, // We're looking forward, so past check not needed
    allowWeekends: false,
    excludeDates,
    publicHolidays
  })) {
    nextDay = addDays(nextDay, 1);
  }

  return nextDay;
};

/**
 * Generate an array of disabled dates for react-datepicker excludeDates prop
 * @param {Object} options - Options for generating disabled dates
 * @param {boolean} options.includePastDates - Include past dates as disabled
 * @param {boolean} options.includeWeekends - Include weekends as disabled
 * @param {Date[]} options.customExcludeDates - Custom dates to exclude
 * @param {Date[]} options.publicHolidays - Public holidays to exclude
 * @param {number} options.futureDays - Number of future days to generate (default: 90)
 * @returns {Date[]} - Array of disabled dates
 */
export const generateExcludedDates = (options = {}) => {
  const {
    includePastDates = true,
    includeWeekends = true,
    customExcludeDates = [],
    publicHolidays = [],
    futureDays = 90
  } = options;

  const excludedDates = [];
  const today = new Date();

  // Add past dates if requested
  if (includePastDates) {
    for (let i = 1; i <= 365; i += 1) { // Go back 1 year
      excludedDates.push(subDays(today, i));
    }
  }

  // Add weekends if requested
  if (includeWeekends) {
    for (let i = 0; i <= futureDays; i += 1) {
      const checkDate = addDays(today, i);
      if (isWeekend(checkDate)) {
        excludedDates.push(checkDate);
      }
    }
  }

  // Add custom excluded dates and public holidays
  excludedDates.push(...customExcludeDates, ...publicHolidays);

  return excludedDates;
};

/**
 * Create a filter function for react-datepicker filterDate prop
 * @param {Object} options - Validation options (same as isValidBookingDate)
 * @returns {Function} - Filter function for react-datepicker
 */
export const createDateFilter = (options = {}) => date => isValidBookingDate(date, options);

/**
 * Get minimum selectable date (typically today or a future date)
 * @param {Date} customMinDate - Custom minimum date (optional)
 * @returns {Date} - The minimum selectable date
 */
export const getMinSelectableDate = (customMinDate = null) => {
  if (customMinDate) {
    return new Date(Math.max(customMinDate.getTime(), new Date().getTime()));
  }
  return new Date();
};

/**
 * Format validation error message for date selection
 * @param {Date} selectedDate - The date that was selected
 * @param {Object} options - Validation options that failed
 * @returns {string} - User-friendly error message
 */
export const getDateValidationError = (selectedDate, options = {}) => {
  const {
    allowPast = false,
    allowWeekends = false
  } = options;

  if (!allowPast && isPastDate(selectedDate)) {
    return "You can only select dates in the future. Please choose an available timeslot.";
  }

  if (!allowWeekends && isWeekend(selectedDate)) {
    return "Weekend dates are not available for booking. Please select a weekday.";
  }

  return "This date is not available for booking. Please select a different date.";
};

// Export default validation options for booking dates
export const DEFAULT_BOOKING_OPTIONS = {
  allowPast: false,
  allowWeekends: false,
  excludeDates: [],
  publicHolidays: []
};

// Export options for different use cases
export const VALIDATION_PRESETS = {
  BOOKING_DATES: {
    allowPast: false,
    allowWeekends: true // Allow weekends, but past dates will still be blocked
  },

  STAFF_LEAVE: {
    allowPast: false,
    allowWeekends: true
  },

  TIME_TRACKING: {
    allowPast: true,
    allowWeekends: true
  },

  REPORTS: {
    allowPast: true,
    allowWeekends: true
  }
};
